import { multiSelect } from "@single-js/common/util/query";
import { differenceInSeconds } from "date-fns";
import _ from "lodash";
import { from, merge, of } from "rxjs";
import { logError } from "util/logging";
import { makeActionCreator } from "worker/api/helpers";
const MEGABYTE_TO_BYTES = 1024 * 1024; // 1 mebibyte in bytes
const SECOND_TO_MS = 1000; // 1 second in milliseconds
const SQL_STATUS_QUERY = `
    SHOW GLOBAL STATUS
`;
const SQL_SELECT_CURRENT_UTC_DATE = `
    SELECT NOW() as currentDate
`;
const getLicenseType = (type) => {
    const licenseType = _.lowerCase(type);
    switch (licenseType) {
        case "developer":
        case "enterprise":
        case "free":
        case "standard":
            return licenseType;
        default:
            return "unknown";
    }
};
// We export this so we can test it.
export const getLicenseInfo = (sqlGlobalStatus, currentDate) => {
    const sqlLicense = _(sqlGlobalStatus)
        .filter((statusRow) => statusRow.Variable_name === "Maximum_cluster_capacity" ||
        statusRow.Variable_name === "Used_cluster_capacity" ||
        statusRow.Variable_name === "License_expiration" ||
        statusRow.Variable_name === "License_key" ||
        statusRow.Variable_name === "License_type" ||
        statusRow.Variable_name === "License_version" ||
        statusRow.Variable_name === "License")
        .keyBy("Variable_name")
        .mapValues("Value")
        .value();
    const { Maximum_cluster_capacity, Used_cluster_capacity, License_expiration, License_key: key, License_type: licenseType, License_version, License: license, } = sqlLicense;
    if (Maximum_cluster_capacity === undefined ||
        Used_cluster_capacity === undefined ||
        License_expiration === undefined ||
        licenseType === undefined ||
        key === undefined ||
        License_version === undefined) {
        return undefined;
    }
    const rawMaxCapacity = Number(Maximum_cluster_capacity.split(" ")[0]); // megabytes
    const rawUsedCapacity = Number(Used_cluster_capacity.split(" ")[0]); // megabytes
    const version = Number(License_version); // 3, 4, …
    const expiration = Number(License_expiration);
    const unitPricing = _.includes(Used_cluster_capacity, "unit");
    let maximumCapacity;
    if (rawMaxCapacity > Number.MAX_SAFE_INTEGER) {
        maximumCapacity = "unlimited";
    }
    else {
        if (unitPricing) {
            maximumCapacity = rawMaxCapacity;
        }
        else {
            maximumCapacity = rawMaxCapacity * MEGABYTE_TO_BYTES;
        }
    }
    let usedCapacity;
    if (unitPricing) {
        usedCapacity = rawUsedCapacity;
    }
    else {
        usedCapacity = rawUsedCapacity * MEGABYTE_TO_BYTES;
    }
    let expirationDate;
    let secondsToExpiration = undefined;
    if (expiration === 0) {
        expirationDate = "unlimited";
    }
    else {
        expirationDate = new Date(expiration * SECOND_TO_MS);
        secondsToExpiration = differenceInSeconds(expirationDate, currentDate);
    }
    return {
        maximumCapacity,
        usedCapacity,
        unitPricing,
        expiration: expirationDate,
        secondsToExpiration,
        key,
        license,
        type: getLicenseType(licenseType),
        version,
    };
};
const createLicenseAction = (sqlGlobalStatus, currentDateResult) => {
    return {
        type: "LICENSE_INFO",
        payload: {
            loading: false,
            data: getLicenseInfo(sqlGlobalStatus, currentDateResult.currentDate),
        },
        error: false,
    };
};
export const queryLicense = makeActionCreator({
    name: "queryLicense",
    handle: (ctx) => {
        const $loading = of({
            type: "LICENSE_INFO",
            error: false,
            payload: { loading: true },
        });
        const $compute = from(ctx.manager
            .getPooledConnection()
            .then((conn) => multiSelect(conn, { sql: SQL_STATUS_QUERY }, { sql: SQL_SELECT_CURRENT_UTC_DATE })
            .then((results) => {
            const [statusRows, dateRows] = results;
            return createLicenseAction(statusRows, dateRows[0]);
        })
            .catch((err) => {
            logError(err);
            return {
                type: "LICENSE_INFO",
                error: true,
                payload: {
                    message: err.message,
                },
            };
        })
            .finally(() => conn.release()))
            .catch((err) => {
            return {
                type: "LICENSE_INFO",
                error: true,
                payload: {
                    message: err.message,
                },
            };
        }));
        return merge($loading, $compute);
    },
});
